import { useCallback, useEffect, useRef, useState } from 'react';
import { collection, doc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';

// "Okudum" onayları (SOP'lerdeki userSOPReadConfirmations'ın karşılığı):
//   userDocumentReadConfirmations/{uid}_{collection}_{docId}
//     userId, docType ('Policies' | 'JobDescriptions'), docId, isRead, readAt / unreadAt
// Belirli id ile yazıldığı için aynı belgeye mükerrer kayıt açılmaz.
const READS_COLLECTION = 'userDocumentReadConfirmations';
export const DOC_TYPES = { policies: 'Policies', jobDescriptions: 'JobDescriptions' };

const readKey = (docType, docId) => `${docType}:${docId}`;

// Policies and JobDescriptions are assigned by role group, same as SOPs:
// a document is visible when its `group` contains the user's role or 'Hepsi'.
const fetchAssigned = async (collectionName, role) => {
  const ref = collection(db, collectionName);
  const [byRole, forAll] = await Promise.all([
    getDocs(query(ref, where('group', 'array-contains', role))),
    getDocs(query(ref, where('group', 'array-contains', 'Hepsi')))
  ]);
  const merged = new Map();
  [...byRole.docs, ...forAll.docs].forEach(d => merged.set(d.id, { id: d.id, ...d.data() }));
  return [...merged.values()];
};

const normalizePolicy = (p) => ({
  id: p.id,
  name: p.name || '',
  version: p.versionNumber,
  subtitle: p.category,
  description: p.description,
  fileUrl: p.fileUrl,
  fileName: p.fileName,
  fileType: p.fileType
});

const normalizeJobDescription = (j) => ({
  id: j.id,
  name: j.title || '',
  version: null,
  subtitle: j.position,
  description: j.description,
  fileUrl: j.fileUrl,
  fileName: j.fileName,
  fileType: j.fileType
});

const byName = (a, b) => a.name.localeCompare(b.name, 'tr');

const fetchReads = async () => {
  const user = auth.currentUser;
  if (!user) return new Set();
  const snapshot = await getDocs(query(collection(db, READS_COLLECTION), where('userId', '==', user.uid)));
  return new Set(snapshot.docs
    .filter((d) => d.data().isRead !== false)
    .map((d) => readKey(d.data().docType, d.data().docId)));
};

const useAssignedDocuments = (role) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [policies, setPolicies] = useState([]);
  const [jobDescriptions, setJobDescriptions] = useState([]);
  const [reads, setReads] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!role) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [policyDocs, jobDocs] = await Promise.all([
          fetchAssigned('Policies', role),
          fetchAssigned('JobDescriptions', role)
        ]);
        if (cancelled) return;
        setPolicies(policyDocs.map(normalizePolicy).sort(byName));
        setJobDescriptions(jobDocs.map(normalizeJobDescription).sort(byName));
      } catch (err) {
        console.error('Atanan politika/görev tanımları yüklenemedi:', err);
        if (!cancelled) setError('load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    // Onaylar okunamazsa belgeler yine listelenir, yalnızca "okunmadı" görünür
    fetchReads()
      .then((set) => { if (!cancelled) setReads(set); })
      .catch((err) => console.error('Doküman okundu onayları yüklenemedi:', err));
    load();
    return () => { cancelled = true; };
  }, [role]);

  const isRead = useCallback((kind, docId) => reads.has(readKey(DOC_TYPES[kind], docId)), [reads]);

  // "Okudum" onayı ver / geri al
  const toggleRead = useCallback(async (kind, docId) => {
    const user = auth.currentUser;
    const docType = DOC_TYPES[kind];
    if (!user || !docType) return;
    const key = readKey(docType, docId);
    const nowRead = !reads.has(key);
    try {
      await setDoc(
        doc(db, READS_COLLECTION, `${user.uid}_${docType}_${docId}`),
        nowRead
          ? { userId: user.uid, docType, docId, isRead: true, readAt: new Date() }
          : { userId: user.uid, docType, docId, isRead: false, unreadAt: new Date() },
        { merge: true }
      );
      setReads((prev) => {
        const next = new Set(prev);
        if (nowRead) next.add(key);
        else next.delete(key);
        return next;
      });
      if (nowRead) toast.success(tRef.current('profilePage.files.toast.docMarkedRead'));
      else toast.info(tRef.current('profilePage.files.toast.docMarkedUnread'));
    } catch (err) {
      console.error('Doküman okundu durumu değiştirilemedi:', err);
      toast.error(tRef.current('profilePage.files.toast.actionError'));
    }
  }, [reads]);

  const countRead = (kind, list) => list.filter((d) => isRead(kind, d.id)).length;

  return {
    policies,
    jobDescriptions,
    loading,
    error,
    isRead,
    toggleRead,
    readCounts: {
      policies: countRead('policies', policies),
      jobDescriptions: countRead('jobDescriptions', jobDescriptions),
    },
  };
};

export default useAssignedDocuments;
